import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ThemeProvider, createTheme, useTheme } from '@mui/material/styles';

const findShell = () =>
  typeof document === 'undefined' ? null : document.querySelector('.jdm');
const shell = () => findShell() || document.body;

/**
 * The `.jdm` shell element, once it exists.
 *
 * MobileApp is a lazy chunk, so a caller OUTSIDE it (App.jsx's app-level
 * popups) can mount before the shell does. MUI resolves a portal container once,
 * at mount — a dialog mounted a moment too early would land in <body>, under the
 * shell, for good. So wait for the shell and only then mount the children.
 */
function useShellHost(enabled) {
  const [host, setHost] = useState(() => (enabled ? findShell() : null));
  useEffect(() => {
    if (!enabled) return undefined;
    const found = findShell();
    if (found) {
      setHost(found);
      return undefined;
    }
    if (typeof MutationObserver === 'undefined' || !document.body) {
      return undefined;
    }
    // MobileShell portals `.jdm` as a direct child of <body>.
    const observer = new MutationObserver(() => {
      const next = findShell();
      if (next) {
        setHost(next);
        observer.disconnect();
      }
    });
    observer.observe(document.body, { childList: true });
    return () => observer.disconnect();
  }, [enabled]);
  return host;
}

/**
 * JED-1425 / JED-1426: a desktop component that opens an MUI dialog (Create a
 * self-assessment area) rendered it into document.body at z-index 1300 — under
 * the mobile shell, which sits at 5000. The dialog was invisible, yet its focus
 * trap still held focus, so the question box behind it stopped taking input.
 *
 * Wrapping such a component here portals its modals and popovers INTO the
 * shell, the same fix MobileBulkFrame uses for the bulk-upload screens, so
 * they stack above mobile content and can be used. Desktop is untouched: the
 * override exists only inside this provider.
 *
 * `intoShell` is for callers rendered OUTSIDE the shell (App.jsx's app-level
 * popups: ClassSubjectsModal, VerifyClassesPopup). It also portals the
 * children's own DOM into `.jdm` — a styled `position: fixed` overlay that is
 * not an MUI modal would otherwise still paint under the shell — and it waits
 * for the shell to exist, so a popup that mounts before the lazy mobile chunk
 * resolves is not stranded in <body>.
 */
export default function MobileMuiLayer({ children, intoShell = false }) {
  const outer = useTheme();
  const theme = useMemo(
    () =>
      createTheme(outer, {
        components: {
          MuiModal: { defaultProps: { container: shell } },
          MuiPopover: { defaultProps: { container: shell } },
        },
      }),
    [outer]
  );
  const host = useShellHost(intoShell);
  const content = <ThemeProvider theme={theme}>{children}</ThemeProvider>;
  if (!intoShell) return content;
  if (!host) return null;
  return createPortal(<div className="jdm-desktop-layer">{content}</div>, host);
}
